import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, TokenResponse, PermissionTuple } from '@/types';

// Check if auth bypass is enabled (for development without Casdoor)
export const AUTH_BYPASS_ENABLED = import.meta.env.VITE_AUTH_BYPASS === 'true';

// Mock user for bypass mode
const BYPASS_USER: User = {
  id: 'bypass-admin',
  email: 'admin@bypass.local',
  name: 'Bypass Admin',
  display_name: 'Admin',
  organization: 'bypass',
  role: 'super_admin',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  permissions: [
    { resource: '*', action: '*' },
    { resource: 'tenant', action: 'manage' },
  ],
};

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isBypassMode: boolean;

  // Actions
  setAuth: (tokens: TokenResponse) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  isTokenExpired: () => boolean;
  getAccessToken: () => string | null;
  initBypassMode: () => void;
  updatePermissions: (permissions: PermissionTuple[], role?: string) => void;
  /** Merges server-owned profile fields (e.g. timezone) after a PATCH. */
  updateUser: (patch: Partial<User>) => void;

  // Legacy support
  login: (user: User, token: string) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      isAuthenticated: AUTH_BYPASS_ENABLED, // Auto-authenticate in bypass mode
      isLoading: !AUTH_BYPASS_ENABLED, // Don't show loading in bypass mode
      isBypassMode: AUTH_BYPASS_ENABLED,

      initBypassMode: () => {
        if (AUTH_BYPASS_ENABLED) {
          set({
            user: BYPASS_USER,
            accessToken: 'bypass-token',
            refreshToken: null,
            expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
            isAuthenticated: true,
            isLoading: false,
            isBypassMode: true,
          });
        }
      },

      setAuth: (tokens: TokenResponse) => {
        const expiresAt = Date.now() + tokens.expires_in * 1000;

        // Store tokens in localStorage for API client
        localStorage.setItem('token', tokens.access_token);
        if (tokens.refresh_token) {
          localStorage.setItem('refresh_token', tokens.refresh_token);
        }

        set({
          user: tokens.user,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token || null,
          expiresAt,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      clearAuth: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (isLoading) => set({ isLoading }),

      updateUser: (patch) => {
        const { user } = get();
        if (!user) return;
        set({ user: { ...user, ...patch } });
      },

      updatePermissions: (permissions, role) => {
        const { user } = get();
        if (!user) return;
        set({
          user: {
            ...user,
            permissions,
            ...(role ? { role } : {}),
          },
        });
      },

      isTokenExpired: () => {
        const { expiresAt } = get();
        if (!expiresAt) return true;
        // Add 5 minute buffer before expiration
        return Date.now() > expiresAt - 5 * 60 * 1000;
      },

      getAccessToken: () => {
        const { accessToken, isTokenExpired } = get();
        if (!accessToken || isTokenExpired()) {
          return null;
        }
        return accessToken;
      },

      // Legacy support for existing code
      login: (user, token) => {
        localStorage.setItem('token', token);
        set({
          user,
          accessToken: token,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        get().clearAuth();
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
        isAuthenticated: state.isAuthenticated,
        isBypassMode: state.isBypassMode,
      }),
    }
  )
);
